import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField
} from '@mui/material';
import { EnhancedCard } from 'components/enhanced/card';
import { useEffect } from 'react';
import { useField, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import {
  CSV_INTERNAL_FIELDS,
  CSV_REQUIRED_FIELDS,
  DEFAULT_CSV_DATE_FORMATS,
  DEFAULT_CSV_DELIMITER,
  DEFAULT_CSV_FORMAT,
  DEFAULT_CSV_MAPPING_COLUMNS
} from '../constants';
import { CsvInternalField, ICsvMapping } from '../types';

interface SupplierCsvMappingFieldsProps {
  disabled?: boolean;
  disableAutoLayout?: boolean;
}

function validateCsvMapping(value: ICsvMapping | undefined): string | undefined {
  if (!value) {
    return 'Configurazione CSV mancante';
  }
  const columns = value.columns || {};
  const mapped = Object.values(columns);
  for (const required of CSV_REQUIRED_FIELDS) {
    if (!mapped.includes(required)) {
      return `Manca la mappatura per il campo obbligatorio "${required}"`;
    }
  }
  if (!mapped.includes('product_code') && !mapped.includes('line_index')) {
    return 'Deve essere mappato almeno uno tra "product_code" e "line_index"';
  }
  return undefined;
}

export default function SupplierCsvMappingFields({ disabled }: SupplierCsvMappingFieldsProps) {
  const { t } = useTranslation();
  const formState = useFormState();
  const csvFormat = formState.values.csv_format ?? DEFAULT_CSV_FORMAT;

  const validate = (value: ICsvMapping | undefined) => {
    if (csvFormat !== 'custom') {
      return undefined;
    }
    return validateCsvMapping(value);
  };

  const {
    input: { value, onChange },
    meta
  } = useField<ICsvMapping | undefined>('csv_mapping', { validate });

  const mapping = value || {};

  useEffect(() => {
    if (csvFormat === 'custom' && !mapping.columns) {
      onChange({
        delimiter: DEFAULT_CSV_DELIMITER,
        date_formats: DEFAULT_CSV_DATE_FORMATS,
        columns: { ...DEFAULT_CSV_MAPPING_COLUMNS }
      });
    }
  }, [csvFormat, mapping.columns, onChange]);

  if (csvFormat !== 'custom') {
    return null;
  }

  const columns = mapping.columns || {};
  const rows = Object.entries(columns);

  const updateMapping = (patch: Partial<ICsvMapping>) => {
    onChange({ ...mapping, ...patch });
  };

  const updateDelimiter = (delimiter: string) => {
    updateMapping({ delimiter });
  };

  const updateDateFormats = (raw: string) => {
    updateMapping({
      date_formats: raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    });
  };

  const addRow = () => {
    updateMapping({
      columns: { ...columns, '': 'order_ref' }
    });
  };

  const removeRow = (csvCol: string) => {
    const next = { ...columns };
    delete next[csvCol];
    updateMapping({ columns: next });
  };

  const updateRowCsvCol = (oldCsvCol: string, newCsvCol: string) => {
    if (oldCsvCol === newCsvCol) {
      return;
    }
    const next: Record<string, CsvInternalField> = {};
    Object.entries(columns).forEach(([k, v]) => {
      if (k === oldCsvCol) {
        next[newCsvCol] = v;
      } else {
        next[k] = v;
      }
    });
    updateMapping({ columns: next });
  };

  const updateRowInternal = (csvCol: string, internal: CsvInternalField) => {
    updateMapping({ columns: { ...columns, [csvCol]: internal } });
  };

  return (
    <Box sx={{ gridColumn: '1 / -1', width: '100%' }}>
      <EnhancedCard
        title={t('entities.supplier.csv_mapping_card_title', 'Struttura e Mappatura File CSV')}
        subtitle={t(
          'entities.supplier.csv_mapping_card_subtitle',
          'Definisci il delimitatore del file e associa i nomi delle colonne del CSV con i campi interni del sistema.'
        )}>
        {/* Sezione Superiore: Parametri Generali del CSV */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              label={t('entities.supplier.csv_delimiter')}
              value={mapping.delimiter ?? DEFAULT_CSV_DELIMITER}
              onChange={(e) => updateDelimiter(e.target.value)}
              disabled={disabled}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              label={t('entities.supplier.csv_date_formats')}
              value={(mapping.date_formats ?? DEFAULT_CSV_DATE_FORMATS).join(',')}
              onChange={(e) => updateDateFormats(e.target.value)}
              disabled={disabled}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>

        <Table
          size="small"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: '0 8px', // Crea un elegante stacco verticale tra le righe
            width: '100%'
          }}>
          <TableHead>
            <TableRow sx={{ '& th': { borderBottom: '2px solid', borderColor: 'divider', pb: 1 } }}>
              <TableCell sx={{ fontWeight: 600, width: '45%', color: 'text.secondary', pl: 1 }}>
                {t('entities.supplier.csv_column_name')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: '45%', color: 'text.secondary' }}>
                {t('entities.supplier.csv_internal_field')}
              </TableCell>
              <TableCell padding="checkbox"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(([csvCol, internal]) => (
              <TableRow
                key={`${csvCol}-${internal}`}
                sx={{
                  // Stile a "scheda" per ogni riga della tabella
                  bgcolor: 'background.paper',
                  '& td': {
                    borderTop: '1px solid',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: 1.5, // Più respiro verticale per i campi input
                    verticalAlign: 'middle'
                  }
                }}>
                <TableCell>
                  <TextField
                    value={csvCol}
                    onChange={(e) => updateRowCsvCol(csvCol, e.target.value)}
                    disabled={disabled}
                    fullWidth
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    value={internal}
                    onChange={(e) => updateRowInternal(csvCol, e.target.value as CsvInternalField)}
                    disabled={disabled}
                    fullWidth
                    size="small">
                    {CSV_INTERNAL_FIELDS.map((field) => (
                      <MenuItem key={field} value={field}>
                        {field}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell padding="checkbox">
                  <IconButton size="small" onClick={() => removeRow(csvCol)} disabled={disabled}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Button
          onClick={addRow}
          disabled={disabled}
          variant="outlined"
          size="small"
          className="mt-2">
          {t('actions.add')}
        </Button>

        {meta.error && (meta.touched || meta.submitFailed) && (
          <FormHelperText
            error
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2, fontSize: '0.875rem' }}>
            <ErrorOutlineIcon fontSize="small" />
            {meta.error}
          </FormHelperText>
        )}
      </EnhancedCard>
    </Box>
  );
}
